import * as React from "react"
import { FiSliders } from "react-icons/fi"
import { IconButton, Icon, Collapse, Box, Text } from "@chakra-ui/react"
import { IItem, ItemFilter, itemMatches, itemValues } from "./Item"
import { moods as _moodOptions } from "./raw-items"
import { FilterMenu } from "./FilterMenu"

export const Filters: React.FC<{
  tripReady: boolean
  filter: ItemFilter
  selectedIds: Set<string>
  setFilter(filter: ItemFilter): void
  setSelectableItems(filteredItems: IItem[]): void
}> = ({ tripReady, filter, selectedIds, setFilter, setSelectableItems }) => {
  const [showMoreFilters, setShowMoreFilters] = React.useState(false)

  const filteredItems = React.useMemo(() => {
    return itemValues.filter((item) => itemMatches(item, filter))
  }, [filter])

  React.useEffect(() => {
    setSelectableItems(
      filteredItems.filter((item) => !selectedIds.has(item.id)),
    )
  }, [filteredItems, selectedIds, setSelectableItems])

  const moodOptions = React.useMemo(() => {
    return _moodOptions
      .map((m) => {
        const augmentedFilter: ItemFilter = {
          ...filter,
          moods: [...(filter.moods || []), m],
        }
        const numItems = filteredItems.reduce((sum: number, currentItem) => {
          const matches = itemMatches(currentItem, augmentedFilter)
          return matches ? sum + 1 : sum
        }, 0)
        return {
          el: (
            <Text as="span" opacity={numItems ? undefined : 0.25}>
              {m}
              <Text as="span" float="right" color="gray.500" pr={4}>
                {numItems}
              </Text>
            </Text>
          ),
          value: m,
          numItems,
        }
      })
      .sort((first, second) => {
        if (
          filter.moods.includes(first.value) &&
          !filter.moods.includes(second.value)
        ) {
          return -1
        }
        return second.numItems - first.numItems
      })
  }, [filter, filteredItems])

  return (
    <Box fontSize={["3xl", null, "5xl", "6xl"]}>
      {tripReady ? "A" : "I want to have a"}{" "}
      <FilterMenu
        big={true}
        options={moodOptions}
        type="checkbox"
        colorScheme="red"
        disabled={tripReady}
        onSelect={(val) =>
          setFilter({
            ...filter,
            moods: val as any,
          })
        }
        minWidth="200px"
        maxHeight="400px"
        overflowY="scroll"
      >
        {filter.moods.length ? (
          filter.moods.join(" and ")
        ) : (
          <Box display="inline-block" w={36}></Box>
        )}
      </FilterMenu>{" "}
      experience with{" "}
      <FilterMenu
        big={true}
        options={["myself", "friends"]}
        closeOnSelect={true}
        colorScheme="purple"
        disabled={tripReady}
        onSelect={(val) =>
          setFilter({
            ...filter,
            withWhom: val as any,
          })
        }
      >
        {filter.withWhom}
      </FilterMenu>
      <IconButton
        ml={4}
        p={4}
        onClick={() => setShowMoreFilters(!showMoreFilters)}
        icon={<Icon as={FiSliders} fontSize="3xl" />}
        aria-label="Show filters"
        variant={showMoreFilters ? "solid" : "ghost"}
        display={tripReady ? "none" : "inline-flex"}
      />
      <Collapse in={showMoreFilters && !tripReady} animateOpacity>
        <Box mt={[null, null, null, null, -4]}>
          <FilterMenu
            options={["indoors", "outdoors", "indoors and outdoors"]}
            closeOnSelect={true}
            colorScheme="blue"
            disabled={tripReady}
            onSelect={(val) =>
              setFilter({
                ...filter,
                location: val as any,
              })
            }
          >
            {filter.location}
          </FilterMenu>{" "}
          <FilterMenu
            options={["today", "later"]}
            closeOnSelect={true}
            colorScheme="cyan"
            disabled={tripReady}
            onSelect={(val) =>
              setFilter({
                ...filter,
                when: val as any,
              })
            }
          >
            {filter.when}
          </FilterMenu>{" "}
          <FilterMenu
            options={["screens ok", "no screens"]}
            closeOnSelect={true}
            colorScheme="teal"
            disabled={tripReady}
            onSelect={(val) =>
              setFilter({
                ...filter,
                noScreens: val === "no screens",
              })
            }
          >
            {filter.noScreens ? "no screens" : "screens ok"}
          </FilterMenu>
        </Box>
      </Collapse>
    </Box>
  )
}
